import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import CheckoutForm from './CheckoutForm';
import Finish from './Finish';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useApiSend } from '../../../utils/httpClient';
import { getPaymentIntent } from '../../../endpoints/payments';
import { useSearchParams } from 'react-router-dom';
import { config } from '../../../utils/config';
import { isDonationAlreadyPaidError } from './donationCheckoutUtils';

const stripePublishableKey = (config.STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

export default function Billing({
  donationId,
  step,
  handleBack,
  donationObject,
  isPaid = false,
  onGoToFinish,
}) {
  let [searchParams] = useSearchParams();

  const [clientSecret, setClientSecret] = useState(null);
  const [getClientSecretError, setGetClientSecretError] = useState(false);
  const [localDonationId, setLocalDonationId] = useState(null);

  const options = {};

  const { mutate } = useApiSend(
    getPaymentIntent,
    (data) => {
      setClientSecret(data.client_secret);
      setGetClientSecretError(false);
    },
    (error) => {
      if (isDonationAlreadyPaidError(error)) {
        onGoToFinish?.();
        return;
      }
      console.error(error);
      setGetClientSecretError(true);
    }
  );

  useEffect(() => {
    if (
      !clientSecret &&
      getClientSecretError === false &&
      step === 2 &&
      donationObject &&
      !isPaid
    ) {
      mutate({
        amount: donationObject.amount,
        donation_id: donationId || localDonationId,
      });
    }
  }, [
    clientSecret,
    getClientSecretError,
    step,
    donationObject,
    donationId,
    localDonationId,
    mutate,
    isPaid,
  ]);

  useEffect(() => {
    if (step === 3) {
      setLocalDonationId(searchParams.get('donationId'));
      setClientSecret(searchParams.get('payment_intent_client_secret'));
    }
  }, [step, searchParams]);

  if (!stripePublishableKey || !stripePromise) {
    return (
      <Grid container justifyContent="center" sx={{ py: 4 }}>
        <Typography
          color="error"
          role="alert"
          sx={{ px: 2, textAlign: 'center' }}
        >
          Online payments are not configured. Please contact support.
        </Typography>
      </Grid>
    );
  }

  if (isPaid && step === 2) {
    return (
      <Grid container justifyContent="center" sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={2}>
          <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
            This donation has already been paid.
          </Typography>
          <Button variant="contained" onClick={() => onGoToFinish?.()}>
            View confirmation
          </Button>
        </Stack>
      </Grid>
    );
  }

  if (step === 3 && isPaid && !clientSecret) {
    return (
      <Grid container justifyContent="center" sx={{ py: 4 }}>
        <Finish paymentAlreadyComplete handleBack={handleBack} />
      </Grid>
    );
  }

  if (!donationObject && step === 2) {
    return (
      <Grid container justifyContent="center" sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress aria-label="Loading donation" />
          <Typography color="text.secondary">Loading your donation…</Typography>
        </Stack>
      </Grid>
    );
  }
  if (!clientSecret) {
    return (
      <Grid container justifyContent="center" sx={{ py: 4 }}>
        <Stack alignItems="center" spacing={2}>
          {getClientSecretError ? (
            <Typography color="error" role="alert">
              We could not start the secure payment session. Please go back and
              try again, or contact support if this continues.
            </Typography>
          ) : (
            <>
              <CircularProgress aria-label="Preparing payment" />
              <Typography color="text.secondary">
                Preparing secure checkout…
              </Typography>
            </>
          )}
        </Stack>
      </Grid>
    );
  } else {
    options.clientSecret = clientSecret;
    return (
      <Elements stripe={stripePromise} options={options}>
        {step === 2 && (
          <Grid container>
            <Grid item xs={12} textAlign={'center'}>
              <CheckoutForm
                goBack={handleBack}
                donationId={donationId}
                hideBack={isPaid}
              />
            </Grid>
          </Grid>
        )}
        {step === 3 && (
          <Finish donationId={localDonationId} handleBack={handleBack} />
        )}
      </Elements>
    );
  }
}
